/*
 * ktechs workshop landing page.
 * Three independent behaviours, no dependencies, no globals:
 *   1. the exploded drawing's part state (pointer, keyboard, tap, deep link)
 *   2. the enquiry form: which intent's fields post, and the structured copy of each enquiry
 *      sent on the side to the ktechs workshops endpoint set on the section
 *   3. the client banner, which the page renders as a still row and this may set moving
 * The drawing hit test reads a baked alpha mask shipped as a theme asset. The
 * reference page read pixels back out of a canvas; on a storefront the plates come
 * from the Shopify CDN, a different origin, so that call can taint the canvas, and
 * it also races image decode. The mask has neither problem and is smaller than one plate.
 */
(function () {
  'use strict';

  var maskPromise = null;

  function loadMask(url) {
    if (!maskPromise) {
      maskPromise = fetch(url, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error(r.status)); })
        .then(function (d) {
          var bits = {};
          d.order.forEach(function (p) {
            var raw = atob(d.masks[p]), arr = new Uint8Array(raw.length), i;
            for (i = 0; i < raw.length; i++) { arr[i] = raw.charCodeAt(i); }
            bits[p] = arr;
          });
          return { w: d.w, h: d.h, order: d.order, bits: bits };
        })
        .catch(function () { return null; });   // the legend buttons still work without it
    }
    return maskPromise;
  }

  function initDrawing(root) {
    if (root.dataset.wsReady === '1') { return; }
    root.dataset.wsReady = '1';

    var stack = root.querySelector('.ktechs-ws-stack');
    if (!stack) { return; }

    var scope = root.closest('.ktechs-ws-drawing') || document,
        imgs = Array.prototype.slice.call(stack.querySelectorAll('img')),
        cards = Array.prototype.slice.call(root.querySelectorAll('.ktechs-ws-callout[data-part]')),
        leads = Array.prototype.slice.call(root.querySelectorAll('.ktechs-ws-leader [data-part], .ktechs-ws-leader__tick[data-part]')),
        legend = Array.prototype.slice.call(scope.querySelectorAll('.ktechs-ws-legend button[data-part]')),
        active = null,
        tapped = false,
        mask = null,
        phone = window.matchMedia('(max-width: 767px)');

    // On a phone the descriptions sit under the drawing in one place that holds the tallest one,
    // so an empty place would be a blank card: the first part is described from the start, and
    // anything that used to clear the pick comes back to it (merchant ruling, 15 Sep).
    function resting() {
      return phone.matches && legend.length ? legend[0].dataset.part : null;
    }

    function apply(part) {
      stack.classList.toggle('is-hovering', !!part);
      imgs.forEach(function (i) { i.classList.toggle('is-active', i.dataset.part === part); });
      cards.forEach(function (c) { c.classList.toggle('is-active', c.dataset.part === part); });
      leads.forEach(function (l) { l.classList.toggle('is-active', l.dataset.part === part); });
      legend.forEach(function (b) {
        var on = b.dataset.part === part;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      active = part;
    }

    function partAt(clientX, clientY) {
      if (!mask) { return null; }
      var r = stack.getBoundingClientRect();
      if (!r.width || !r.height) { return null; }
      var x = Math.floor((clientX - r.left) / r.width * mask.w),
          y = Math.floor((clientY - r.top) / r.height * mask.h),
          idx, i, b;
      if (x < 0 || y < 0 || x >= mask.w || y >= mask.h) { return null; }
      idx = y * mask.w + x;
      for (i = 0; i < mask.order.length; i++) {
        b = mask.bits[mask.order[i]];
        if (b && (b[idx >> 3] & (0x80 >> (idx & 7)))) { return mask.order[i]; }
      }
      return null;
    }

    // Chrome decodes an image when it first paints it, and a plate at opacity 0 is never
    // painted, so the first hover waited on a decode and showed an empty tier for a moment.
    // Warm all three solids once the page has finished loading, so nothing competes with
    // the critical resources, and again the moment a pointer reaches the figure in case
    // the load event has not landed yet.
    var warmed = false;
    function warmSolids() {
      if (warmed) { return; }
      warmed = true;
      stack.querySelectorAll('img.ktechs-ws-solid').forEach(function (img) {
        if (img.decode) { img.decode().catch(function () { warmed = false; }); }
      });
    }
    if (document.readyState === 'complete') { warmSolids(); }
    else { window.addEventListener('load', warmSolids, { once: true }); }
    root.addEventListener('pointerenter', warmSolids);

    legend.forEach(function (btn) {
      btn.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') { apply(btn.dataset.part); } });
      btn.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'touch') { apply(resting()); } });
      btn.addEventListener('focus', function () { apply(btn.dataset.part); });
      btn.addEventListener('blur', function () { apply(resting()); });
      btn.addEventListener('click', function () {
        if (active === btn.dataset.part && tapped) { apply(resting()); } else { apply(btn.dataset.part); }
        tapped = true;
      });
    });

    loadMask(root.dataset.hitmask).then(function (m) {
      mask = m;
      if (!mask) { return; }
      stack.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') { return; }
        var p = partAt(e.clientX, e.clientY);
        stack.style.cursor = p ? 'pointer' : 'default';
        if (p !== active) { apply(p); }
      });
      stack.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'touch') { apply(resting()); } });
      stack.addEventListener('click', function (e) {
        var p = partAt(e.clientX, e.clientY);
        tapped = true;
        apply(!p || p === active ? resting() : p);
      });
    });

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { apply(resting()); } });

    // #ws-caps / #ws-switches / #ws-base open the drawing on one part, for review links
    var hash = (window.location.hash || '').replace('#ws-', '');
    if (['caps', 'switches', 'base'].indexOf(hash) >= 0) { tapped = true; apply(hash); }
    else { apply(resting()); }
    function onWidth() { if (!tapped || !active) { apply(resting()); } }
    if (phone.addEventListener) { phone.addEventListener('change', onWidth); }
    else if (phone.addListener) { phone.addListener(onWidth); }
  }

  // After Shopify posts the enquiry form the page comes back at ?contact_posted=true#WsEnquiry
  // (or with errors), and the section has switched the first jump to instant (style#WsLand).
  // Once the page has loaded and its images have settled, the form is brought into view one
  // last time at its final position, and smooth scrolling is given back.
  function landOnForm() {
    var land = document.getElementById('WsLand');
    if (!land) { return; }
    var target = document.getElementById((location.hash || '#WsEnquiry').slice(1)) || document.getElementById('WsEnquiry');
    if (!target) { return; }
    var go = function () { target.scrollIntoView({ block: 'start', behavior: 'auto' }); };
    // The browser's own first jump lands short, as the content above the form is still taking
    // its size; so the form is held at the top of the viewport, frame by frame, for a moment
    // after load, while everything above it finishes sizing out of view. Every scroll here is
    // instant (the rule in the page), which is also what reduced motion asks for.
    var until = 0;
    var hold = function () {
      go();
      if (performance.now() < until) { requestAnimationFrame(hold); }
      else if (land.parentNode) { land.parentNode.removeChild(land); }
    };
    var settle = function () { until = performance.now() + 1800; requestAnimationFrame(hold); };
    go();
    if (document.readyState === 'complete') { settle(); } else { window.addEventListener('load', settle); }
  }

  function initForm(root) {
    if (root.dataset.wsReady === '1') { return; }
    root.dataset.wsReady = '1';
    var form = root.querySelector('form');
    if (!form) { return; }

    // Which intent's fields post. CSS hides the other groups, but a hidden input still
    // submits, and a call enquiry carrying a package choice reads as noise.
    function applyIntent() {
      var chosen = root.querySelector('input[data-intent]:checked'),
          keep = chosen ? chosen.getAttribute('data-intent') : null;
      root.querySelectorAll('[data-only]').forEach(function (group) {
        var off = group.getAttribute('data-only') !== keep;
        group.querySelectorAll('input, select, textarea').forEach(function (field) { field.disabled = off; });
      });
    }
    var val = function (name) {
      var el = form.querySelector('[name="' + name + '"]:not(:disabled)');
      return el ? el.value : '';
    };
    var checked = function (name) {
      var el = form.querySelector('[name="' + name + '"]:checked:not(:disabled)');
      return el ? el.value : '';
    };
    var token = function () {
      var el = form.querySelector('[name="cf-turnstile-response"]');
      return el ? el.value : '';
    };

    // A structured copy of the enquiry goes to ktechs workshops when the section has an
    // endpoint. It is sent on the side: whatever happens to the copy, the email is not held
    // back. The copy never goes without the Turnstile token the endpoint checks, a field the
    // chosen intent does not use is left out as in the email, and the same content is sent
    // once. The endpoint is read when the form is sent, so an empty setting sends nothing.
    var lastSent = '';
    function sendCopy() {
      var endpoint = root.getAttribute('data-ws-endpoint') || '';
      if (!endpoint || !token()) { return Promise.resolve(); }
      var payload = {
        name: val('contact[name]'), email: val('contact[email]'), phone: val('contact[phone]'),
        company: val('contact[Company or group]'), guests: val('contact[How many guests]'),
        callTime: val('contact[Good time to call]'), type: checked('contact[Enquiry type]'),
        where: checked('contact[Where]'), package: checked('contact[Package]'),
        notes: val('contact[body]'), turnstile: token()
      };
      var body = JSON.stringify(payload);
      if (body === lastSent) { return Promise.resolve(); }
      lastSent = body;
      try {
        return fetch(endpoint, {
          method: 'POST', mode: 'cors', keepalive: true,
          headers: { 'Content-Type': 'text/plain' }, body: body
        }).catch(function () {});
      } catch (e) { return Promise.resolve(); }
    }

    // The Turnstile token, once it is there: the widget fills its field by itself, usually
    // within a second of the page loading, and shows itself only when Cloudflare wants a
    // tick. A visitor who presses the button before that waits here, up to the limit.
    function whenToken(limitMs) {
      return new Promise(function (resolve) {
        var started = Date.now();
        (function look() {
          if (token()) { resolve(true); }
          else if (Date.now() - started > limitMs) { resolve(false); }
          else { setTimeout(look, 150); }
        })();
      });
    }

    // Shopify's own reCAPTCHA, when the store's spam protection is on, binds this form and
    // lets only its own script post it: it stops the native submit, runs its check and sends
    // the form itself, and the page reloads with Shopify's notice. That path is left alone.
    var boundByShopify = function () { return form.dataset.recaptchaBound === 'true' || form.dataset.hcaptchaBound === 'true'; };

    var button = form.querySelector('button[type="submit"]'),
        sentNote = form.querySelector('[data-ws-sent]'),
        sending = false,
        fallingBack = false;

    function finish(ok) {
      sending = false;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
      if (!ok) { return; }
      form.reset();
      applyIntent();
      if (window.turnstile) { try { window.turnstile.reset(); } catch (e) { /* a fresh token comes with the next load */ } }
      if (sentNote) {
        sentNote.hidden = false;
        sentNote.scrollIntoView({ block: 'nearest' });
        sentNote.focus();
      }
    }

    // Sends the form itself: Shopify's contact endpoint, which sends the email, answered in
    // the background so the page stays where it is. Shopify refuses the post when its own
    // captcha is on (it answers with no token), and a failed post of any kind falls back on
    // the native submit, so the email is never lost to a quiet failure.
    function sendNative() {
      return fetch(form.getAttribute('action') || '/contact', {
        method: 'POST', credentials: 'same-origin', redirect: 'follow',
        headers: { Accept: 'text/html' }, body: new FormData(form)
      }).then(function (r) {
        return r.ok && !/\/challenge/.test(r.url) ? r.text().then(function (html) {
          return /form-notification success|posted_successfully|contact_posted=true/.test(html) || r.redirected;
        }) : false;
      }).catch(function () { return false; });
    }

    form.addEventListener('submit', function (event) {
      if (fallingBack) { return; }           // the native submit, left to the browser
      applyIntent();
      if (boundByShopify()) { sendCopy(); return; }   // Shopify's script sends the form
      event.preventDefault();
      if (sending) { return; }
      sending = true;
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      whenToken(20000).then(function (has) {
        if (!has) { return false; }
        return Promise.all([sendCopy(), sendNative()]).then(function (results) { return results[1]; });
      }).then(function (ok) {
        if (ok) { finish(true); return; }
        // the background post did not go through: the browser sends the form the usual way,
        // through a submit event, so a captcha script bound to the form still gets its turn
        finish(false);
        fallingBack = true;
        if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
      });
    });
  }

  function setIntent(intent) {
    var el = document.querySelector('input[data-intent="' + intent + '"]');
    if (el && !el.checked) {
      el.checked = true;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---- The client banner ----
     Each client is in the page once and stays that way. Nothing here copies the row.

     The row is wider than the rail, so there is always more of it than fits, and it can
     simply be slid: the offset grows, and the moment a logo has gone fully past the left
     edge that same element is moved to the end of the row and the offset is reduced by
     exactly the width it took with it. The row is unchanged in length and the picture is
     unchanged in position, so it carries on forever with twelve elements and no seam. A
     copy is only needed when the row is narrower than the rail, which is why the row being
     narrower is the case that holds still instead.

     Owning the position outright is what makes the rest behave. There is no keyframe to
     restart, so a page relayout cannot throw the row back to its beginning, and holding it
     still is a matter of slowing the travel rather than freezing a rule. */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initMarquee(vp) {
    if (vp.dataset.wsReady === '1') { return; }
    vp.dataset.wsReady = '1';

    var track = vp.querySelector('.ktechs-ws-marquee__track'),
        set = vp.querySelector('[data-ws-marquee-set]');
    if (!track || !set) { return; }

    var SPEED = Math.min(Math.max(parseFloat(vp.getAttribute('data-speed')) || 55, 10), 200),
        EASE = 0.9;                 // seconds to go from travelling to still, and back

    var offset = 0, gap = 0, firstW = 0,
        speed = SPEED, want = SPEED,   // pixels a second, current and wanted
        frame = 0, stamp = 0,
        onScreen = true, hovered = false, focused = false, live = false;

    function measure() {
      gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      firstW = set.firstElementChild.getBoundingClientRect().width + gap;
    }

    function step(now) {
      var dt = stamp ? Math.min((now - stamp) / 1000, 0.05) : 0.016;
      stamp = now;

      /* A steady change of speed rather than an eased one, because a spring sheds most of
         its speed in the first fraction of a second and still reads as stopping dead. This
         loses the same amount every frame, which is what slowing down looks like. */
      var change = (SPEED / EASE) * dt;
      if (speed < want) { speed = Math.min(speed + change, want); }
      else if (speed > want) { speed = Math.max(speed - change, want); }

      offset += speed * dt;

      // Whatever has gone fully past the left edge goes to the back, and the offset gives
      // back exactly what it took, so nothing moves on screen at the moment of the swap.
      var guard = 0;
      while (offset >= firstW && guard++ < 40) {
        offset -= firstW;
        set.appendChild(set.firstElementChild);
        firstW = set.firstElementChild.getBoundingClientRect().width + gap;
      }

      track.style.transform = 'translate3d(' + (-offset).toFixed(2) + 'px, 0, 0)';

      if (speed === 0 && want === 0) { frame = 0; stamp = 0; return; }   // nothing to draw
      frame = window.requestAnimationFrame(step);
    }

    function run() {
      if (!live || !onScreen) { return; }
      if (!frame) { stamp = 0; frame = window.requestAnimationFrame(step); }
    }
    function halt() {
      if (frame) { window.cancelAnimationFrame(frame); frame = 0; stamp = 0; }
    }
    function settle() {
      want = (hovered || focused) ? 0 : SPEED;
      run();
    }

    function layout() {
      var rail = vp.clientWidth;
      if (reduceMotion.matches || !rail || !set.firstElementChild) { stop(); return; }

      // Measured on one line, which is how it would be held if it travelled.
      var was = vp.getAttribute('data-marquee');
      vp.setAttribute('data-marquee', 'running');
      var rowW = set.getBoundingClientRect().width;

      // A row no wider than the rail is already all on screen and has nothing to show by
      // moving, and is also the one shape this cannot slide without running out.
      if (rowW <= rail) { vp.setAttribute('data-marquee', was || ''); stop(); return; }

      measure();
      if (!live) { live = true; offset = 0; }
      track.style.transform = 'translate3d(' + (-offset).toFixed(2) + 'px, 0, 0)';
      settle();
    }

    function stop() {
      live = false;
      halt();
      vp.removeAttribute('data-marquee');
      track.style.transform = '';
      offset = 0;
    }

    var pending = null, lastRail = -1;
    function relayout() {
      window.clearTimeout(pending);
      pending = window.setTimeout(function () { lastRail = vp.clientWidth; layout(); }, 120);
    }

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        if (vp.clientWidth === lastRail) { return; }
        lastRail = vp.clientWidth;
        relayout();
      }).observe(vp);
    } else {
      window.addEventListener('resize', relayout);
    }

    // A logo has no width until its image lands, and the row is only as right as the
    // widths it was measured from.
    vp.querySelectorAll('img').forEach(function (img) {
      if (img.complete) { return; }
      img.addEventListener('load', function () { if (live) { measure(); } else { relayout(); } }, { once: true });
      img.addEventListener('error', function () { if (live) { measure(); } else { relayout(); } }, { once: true });
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { if (live) { measure(); } else { relayout(); } });
    }

    if (reduceMotion.addEventListener) { reduceMotion.addEventListener('change', layout); }
    else if (reduceMotion.addListener) { reduceMotion.addListener(layout); }

    /* Touch is left out: a finger passing over on the way down the page is not a request to
       hold still, and a cancelled touch can leave without ever saying so. */
    vp.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') { return; }
      hovered = true; settle();
    });
    vp.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') { return; }
      hovered = false; settle();
    });
    vp.addEventListener('pointercancel', function () { hovered = false; settle(); });
    vp.addEventListener('focusin', function () { focused = true; settle(); });
    vp.addEventListener('focusout', function () { focused = false; settle(); });

    // Drawing something nobody is looking at is only heat.
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) { run(); } else { halt(); }
      }, { rootMargin: '200px' }).observe(vp);
    }

    layout();
  }

  function boot() {
    landOnForm();
    document.querySelectorAll('[data-ktechs-ws-drawing]').forEach(initDrawing);
    document.querySelectorAll('[data-ktechs-ws-form]').forEach(initForm);
    document.querySelectorAll('[data-ktechs-ws-marquee]').forEach(initMarquee);

    // A link that carries an intent arrives at a form already set to it, so "ask about the
    // other packages" lands on the proposal form rather than on whatever was last checked.
    document.querySelectorAll('[data-ws-intent]').forEach(function (link) {
      if (link.dataset.wsIntentBound === '1') { return; }
      link.dataset.wsIntentBound = '1';
      link.addEventListener('click', function () { setIntent(link.getAttribute('data-ws-intent')); });
    });
    var m = /^#ask-(call|proposal)$/.exec(window.location.hash || '');
    if (m) { setIntent(m[1]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);   // theme editor re-render
})();
